import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { FeatureGateModalComponent } from 'src/shared/components/feature-gate-modal/feature-gate-modal.component';

/**
 * Gates features that ship in the UI but aren't enabled for the account yet.
 *
 * The long-term-rental work is shipping ahead of its backend, so the surfaces are all
 * visible but most writes can't actually run. Rather than hide them (which would lose
 * the discovery value) each entry point calls {@link gate}, which shows a "reach out to
 * enable this" prompt.
 *
 * Usage mirrors DemoBlockService.block() — gate FIRST, abort if it returns true:
 *
 *   addListing() {
 *     if (this.featureGate.gate()) { return; }
 *     ...the real action...
 *   }
 *
 * This fires for EVERY account (the feature genuinely isn't built), so only wire it
 * into actions that truly cannot complete. Which prompt appears depends on who's
 * looking: the shared demo session has no account manager, so it gets the demo
 * "create an account" prompt; real RENTAL accounts see the account-manager one; every
 * other account type gets a generic "contact us" prompt that lands in the chat, since
 * only rentals have an account manager to be handed off to.
 */
@Injectable({ providedIn: 'root' })
export class FeatureGateService {

  private modalController = inject(ModalController);
  private demoBlock = inject(DemoBlockService);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);

  private isPromptOpen = false;

  /** Shows the "reach out to enable this" prompt and returns true so the caller aborts. */
  gate(): boolean {
    // Demo sessions always get the "create an account to get started" prompt —
    // the account-manager prompt only makes sense once they have a real account.
    if (this.demoBlock.block()) {
      return true;
    }
    this.showPrompt();
    return true;
  }

  private async showPrompt(): Promise<void> {
    // Guard against double-taps and against two gated controls firing at once.
    if (this.isPromptOpen) {
      return;
    }
    this.isPromptOpen = true;
    const modal = await this.modalController.create({
      component: FeatureGateModalComponent,
      cssClass: 'demo-block-modal-wrapper',
      backdropDismiss: true
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    this.isPromptOpen = false;
    // Non-rental "Contact Us" — routed here rather than in the modal so the navigation
    // happens after the modal is fully torn down.
    if (data?.contactUs) {
      if (this.rightSidePanelService.isOpen$.getValue()) {
        this.rightSidePanelService.closeRightSidePanel();
      }
      this.navCtrl.navigateForward('chat');
    }
  }
}
